import { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { getConnectionStats, logAction } from '../../lib/audit';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    ClipboardList,
    Search,
    Clock,
    User,
    Activity,
    ShieldAlert,
    Calendar,
    Filter,
    RefreshCcw,
    Loader2,
    Monitor,
    Smartphone,
    Tablet,
    Globe,
    LogIn,
    LogOut,
    TrendingUp,
    Users
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

export default function AuditTrail() {
    const { isAdmin, user } = useAuth();
    const [activeTab, setActiveTab] = useState('audit');
    const [logs, setLogs] = useState([]);
    const [connectionStats, setConnectionStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        if (isAdmin) {
            fetchData();
        }
    }, [isAdmin, activeTab]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            if (activeTab === 'audit') {
                await fetchAuditLogs();
            } else {
                await fetchConnectionStats();
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            // Surfacer la cause au lieu de l'avaler silencieusement (sinon la page
            // paraît "vide" / "ne marche pas"). Cas fréquent : permission Firestore
            // refusée (règles non déployées) ou index manquant.
            const message = err?.code === 'permission-denied'
                ? "Accès refusé par Firestore. Vérifiez que les règles de sécurité sont déployées et que votre compte a le rôle « admin »."
                : err?.code === 'failed-precondition'
                    ? "Index Firestore manquant. Ouvrez la console (F12) : le message d'erreur contient un lien pour créer l'index requis."
                    : (err?.message || 'Erreur lors du chargement des journaux.');
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const fetchAuditLogs = async () => {
        const q = query(
            collection(db, 'audit_trail'),
            orderBy('timestamp', 'desc'),
            limit(100)
        );
        const snapshot = await getDocs(q);
        const logsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate() || new Date()
        }));
        setLogs(logsData);
    };

    const fetchConnectionStats = async () => {
        // Fetch stats for the last 30 days
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);

        const stats = await getConnectionStats(startDate, endDate);
        setConnectionStats(stats);
    };

    const handleTestLog = async () => {
        if (!user) return;
        try {
            await logAction(user.uid, user.email, 'TEST_ACTION', 'System Check', { test: true, timestamp: new Date().toISOString() });
            toast.success('Entrée de test ajoutée. Actualisez pour voir.');
            fetchData();
        } catch (error) {
            console.error('Test log failed:', error);
            toast.error('Échec du test de log');
        }
    };

    const getActionColor = (action) => {
        if (action.includes('DELETE')) return 'bg-red-100 text-red-600 border-red-200';
        if (action.includes('CREATE') || action.includes('ADD')) return 'bg-emerald-100 text-emerald-600 border-emerald-200';
        if (action.includes('UPDATE')) return 'bg-blue-100 text-blue-600 border-blue-200';
        if (action.includes('ROLE')) return 'bg-purple-100 text-purple-600 border-purple-200';
        return 'bg-slate-100 text-slate-600 border-slate-200';
    };

    const filteredAuditLogs = logs.filter(log => {
        const searchMatch =
            log.userEmail?.toLowerCase().includes(search.toLowerCase()) ||
            log.action?.toLowerCase().includes(search.toLowerCase()) ||
            log.target?.toLowerCase().includes(search.toLowerCase());

        if (filter === 'all') return searchMatch;
        return searchMatch && log.action.includes(filter.toUpperCase());
    });

    if (!isAdmin) {
        return (
            <div className="flex flex-col items-center justify-center py-20">
                <ShieldAlert className="h-16 w-16 text-destructive mb-4" />
                <h2 className="text-2xl font-bold">Accès refusé</h2>
                <p className="text-muted-foreground">Seuls les administrateurs peuvent consulter les logs d'audit.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-10">
            <div className="relative overflow-hidden rounded-3xl bg-slate-900 p-8 text-white shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 via-transparent to-emerald-500/20 opacity-50" />
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 text-indigo-400">
                            <Activity className="h-5 w-5" />
                            <span className="text-sm font-bold uppercase tracking-widest">Centre de Contrôle</span>
                        </div>
                        <h1 className="text-3xl md:text-4xl font-black">Audit & Logs</h1>
                        <p className="text-slate-400 max-w-md">Surveillance complète de l'activité du système et des connexions utilisateurs.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button
                            onClick={handleTestLog}
                            variant="destructive"
                            className="bg-red-500/10 border-red-500/20 hover:bg-red-500/20 text-red-400 gap-2 rounded-2xl h-12 border"
                        >
                            <ShieldAlert className="h-4 w-4" />
                            Test Audit
                        </Button>
                        <Button
                            onClick={fetchData}
                            variant="outline"
                            className="bg-white/5 border-white/10 hover:bg-white/10 text-white gap-2 rounded-2xl h-12"
                        >
                            <RefreshCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                            Actualiser
                        </Button>
                    </div>
                </div>
            </div>

            {error && (
                <Card className="rounded-2xl border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
                    <CardContent className="p-4 flex items-start gap-3">
                        <ShieldAlert className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
                        <div>
                            <p className="font-semibold text-red-700 dark:text-red-300">Impossible de charger les journaux</p>
                            <p className="text-sm text-red-600 dark:text-red-400 mt-0.5">{error}</p>
                        </div>
                    </CardContent>
                </Card>
            )}

            <Tabs defaultValue="audit" value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <div className="flex justify-center md:justify-start">
                    <TabsList className="bg-white dark:bg-slate-900 p-1 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
                        <TabsTrigger value="audit" className="rounded-xl px-6 py-2.5 data-[state=active]:bg-slate-900 data-[state=active]:text-white transition-all">
                            <ClipboardList className="mr-2 h-4 w-4" />
                            Journal d'Audit
                        </TabsTrigger>
                        <TabsTrigger value="connections" className="rounded-xl px-6 py-2.5 data-[state=active]:bg-indigo-600 data-[state=active]:text-white transition-all">
                            <Monitor className="mr-2 h-4 w-4" />
                            Connexions
                        </TabsTrigger>
                    </TabsList>
                </div>

                <TabsContent value="audit" className="space-y-6">
                    <Card className="rounded-[2rem] border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden">
                        <CardHeader className="border-b bg-slate-50/50 dark:bg-slate-900/50">
                            <div className="flex flex-col md:flex-row gap-4 justify-between">
                                <div className="relative flex-1">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                    <Input
                                        placeholder="Rechercher par utilisateur, action ou cible..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="pl-12 rounded-2xl border-slate-200 h-12"
                                    />
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    {['ALL', 'CREATE', 'UPDATE', 'DELETE', 'ROLE'].map((f) => (
                                        <Button
                                            key={f}
                                            variant={filter === f.toLowerCase() ? 'default' : 'outline'}
                                            size="sm"
                                            onClick={() => setFilter(f.toLowerCase())}
                                            className="rounded-xl text-xs font-bold"
                                        >
                                            {f === 'ALL' ? 'Tous' : f}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0">
                            {loading && logs.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-20">
                                    <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
                                    <p className="text-muted-foreground animate-pulse">Chargement des logs...</p>
                                </div>
                            ) : filteredAuditLogs.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="hover:bg-transparent">
                                                <TableHead className="w-[180px]">Date & Heure</TableHead>
                                                <TableHead>Administrateur</TableHead>
                                                <TableHead>Action</TableHead>
                                                <TableHead>Cible</TableHead>
                                                <TableHead className="text-right">Détails</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {filteredAuditLogs.map((log) => (
                                                <TableRow key={log.id} className="group hover:bg-slate-50/80 dark:hover:bg-slate-900 transition-colors">
                                                    <TableCell className="font-medium">
                                                        <div className="flex flex-col">
                                                            <span className="text-slate-900 dark:text-slate-100 text-sm">
                                                                {log.timestamp.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                                                            </span>
                                                            <span className="text-slate-400 text-[11px] flex items-center gap-1">
                                                                <Clock className="h-3 w-3" />
                                                                {log.timestamp.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center overflow-hidden">
                                                                <User className="h-4 w-4 text-slate-500" />
                                                            </div>
                                                            <span className="text-slate-600 dark:text-slate-300 text-sm font-medium">{log.userEmail}</span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline" className={`rounded-lg uppercase text-[10px] font-black tracking-widest py-1 border-opacity-50 ${getActionColor(log.action)}`}>
                                                            {log.action}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="text-slate-900 dark:text-slate-100 font-bold text-sm">{log.target}</span>
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex justify-end gap-1">
                                                            {log.details && Object.entries(log.details).map(([key, value]) => (
                                                                <Badge key={key} variant="secondary" className="text-[10px] bg-slate-100 text-slate-500 border-0">
                                                                    {key}: {String(value)}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="py-24 text-center">
                                    <div className="w-20 h-20 rounded-[2rem] bg-slate-50 flex items-center justify-center mx-auto mb-6">
                                        <ClipboardList className="h-10 w-10 text-slate-200" />
                                    </div>
                                    <h3 className="text-xl font-bold">Aucun log trouvé</h3>
                                    <p className="text-slate-500 mt-2">Aucune action ne correspond à vos critères de recherche.</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="connections" className="space-y-6">
                    {loading && !connectionStats ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
                            <p className="text-muted-foreground animate-pulse">Analyse des connexions...</p>
                        </div>
                    ) : connectionStats ? (
                        <>
                            {/* KPI Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <Card className="rounded-2xl border-slate-200 dark:border-slate-800 shadow-sm bg-gradient-to-br from-indigo-50 to-white dark:from-slate-900 dark:to-slate-800">
                                    <CardContent className="pt-6">
                                        <div className="flex justify-between items-start mb-4">
                                            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl">
                                                <TrendingUp className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                                            </div>
                                            <Badge variant="outline" className="bg-white/50 dark:bg-slate-800 text-xs font-normal">30 derniers jours</Badge>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Total Connexions</p>
                                            <h3 className="text-2xl font-black text-slate-900 dark:text-white">{connectionStats.totalConnections}</h3>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card className="rounded-2xl border-slate-200 dark:border-slate-800 shadow-sm bg-gradient-to-br from-emerald-50 to-white dark:from-slate-900 dark:to-slate-800">
                                    <CardContent className="pt-6">
                                        <div className="flex justify-between items-start mb-4">
                                            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl">
                                                <Users className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                                            </div>
                                            <Badge variant="outline" className="bg-white/50 dark:bg-slate-800 text-xs font-normal">Actifs</Badge>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Utilisateurs Uniques</p>
                                            <h3 className="text-2xl font-black text-slate-900 dark:text-white">{connectionStats.uniqueUsers}</h3>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card className="rounded-2xl border-slate-200 dark:border-slate-800 shadow-sm bg-gradient-to-br from-blue-50 to-white dark:from-slate-900 dark:to-slate-800">
                                    <CardContent className="pt-6">
                                        <div className="flex justify-between items-start mb-4">
                                            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                                                <Smartphone className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                            </div>
                                            <Badge variant="outline" className="bg-white/50 dark:bg-slate-800 text-xs font-normal">Mobile</Badge>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Via Mobile</p>
                                            <h3 className="text-2xl font-black text-slate-900 dark:text-white">
                                                {connectionStats.connectionsByDevice?.mobile || 0}
                                                <span className="text-sm font-normal text-slate-400 ml-1">
                                                    ({connectionStats.totalConnections ? Math.round(((connectionStats.connectionsByDevice.mobile || 0) / connectionStats.totalConnections) * 100) : 0}%)
                                                </span>
                                            </h3>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card className="rounded-2xl border-slate-200 dark:border-slate-800 shadow-sm bg-gradient-to-br from-purple-50 to-white dark:from-slate-900 dark:to-slate-800">
                                    <CardContent className="pt-6">
                                        <div className="flex justify-between items-start mb-4">
                                            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                                                <Monitor className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                                            </div>
                                            <Badge variant="outline" className="bg-white/50 dark:bg-slate-800 text-xs font-normal">Desktop</Badge>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Via Ordinateur</p>
                                            <h3 className="text-2xl font-black text-slate-900 dark:text-white">
                                                {connectionStats.connectionsByDevice?.desktop || 0}
                                                <span className="text-sm font-normal text-slate-400 ml-1">
                                                    ({connectionStats.totalConnections ? Math.round(((connectionStats.connectionsByDevice.desktop || 0) / connectionStats.totalConnections) * 100) : 0}%)
                                                </span>
                                            </h3>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Activity Chart - Simple Visual Representation */}
                                <Card className="rounded-2xl border-slate-200 dark:border-slate-800 shadow-sm lg:col-span-2">
                                    <CardHeader>
                                        <CardTitle className="text-lg">Activité Quotidienne</CardTitle>
                                        <CardDescription>Volume des connexions sur les 30 derniers jours</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="h-[200px] w-full flex items-end gap-1 overflow-hidden pt-4">
                                            {connectionStats.dailyTrend.length > 0 ? (
                                                connectionStats.dailyTrend.map((day, i) => (
                                                    <div key={i} className="flex-1 flex flex-col items-center group relative">
                                                        <div
                                                            className="w-full bg-indigo-500 rounded-t-sm hover:bg-indigo-400 transition-all relative"
                                                            style={{
                                                                height: `${Math.max(10, (day.count / Math.max(...connectionStats.dailyTrend.map(d => d.count))) * 100)}%`,
                                                                minHeight: '4px'
                                                            }}
                                                        >
                                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                                                                {new Date(day.date).toLocaleDateString()} : {day.count}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm">
                                                    Pas assez de données
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex justify-between mt-2 text-xs text-slate-400">
                                            <span>Il y a 30 jours</span>
                                            <span>Aujourd'hui</span>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Top Users */}
                                <Card className="rounded-2xl border-slate-200 dark:border-slate-800 shadow-sm">
                                    <CardHeader>
                                        <CardTitle className="text-lg">Utilisateurs Actifs</CardTitle>
                                        <CardDescription>Top 5 des connexions</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-4">
                                            {connectionStats.topUsers.slice(0, 5).map((user, i) => (
                                                <div key={user.userId} className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-bold text-xs text-slate-600">
                                                        {i + 1}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium truncate">{user.fullName || user.email}</p>
                                                        <p className="text-xs text-slate-400">Dernière: {user.lastConnection?.toLocaleDateString()}</p>
                                                    </div>
                                                    <Badge variant="secondary">{user.count}</Badge>
                                                </div>
                                            ))}
                                            {connectionStats.topUsers.length === 0 && (
                                                <p className="text-sm text-slate-400 text-center py-4">Aucune donnée</p>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Recent Connections Table */}
                            <Card className="rounded-[2rem] border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden">
                                <CardHeader className="border-b bg-slate-50/50 dark:bg-slate-900/50">
                                    <CardTitle>Historique des Connexions</CardTitle>
                                    <CardDescription>Journal détaillé des accès récents au système</CardDescription>
                                </CardHeader>
                                <CardContent className="p-0">
                                    <div className="overflow-x-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Date & Heure</TableHead>
                                                    <TableHead>Utilisateur</TableHead>
                                                    <TableHead>Action</TableHead>
                                                    <TableHead>Appareil</TableHead>
                                                    <TableHead className="text-right">Navigateur</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {connectionStats.recentLogs.map((log) => (
                                                    <TableRow key={log.id} className="hover:bg-slate-50/50">
                                                        <TableCell className="font-medium">
                                                            <div className="flex flex-col">
                                                                <span className="text-sm text-slate-900">{log.timestamp.toLocaleDateString()}</span>
                                                                <span className="text-xs text-slate-400">{log.timestamp.toLocaleTimeString()}</span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center">
                                                                    <User className="h-3 w-3 text-slate-500" />
                                                                </div>
                                                                <span className="text-sm font-medium">{log.fullName || log.userEmail}</span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            {log.action === 'LOGIN' || log.action === 'REGISTER_LOGIN' ? (
                                                                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-0">
                                                                    <LogIn className="h-3 w-3 mr-1" /> Connexion
                                                                </Badge>
                                                            ) : (
                                                                <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-200 border-0">
                                                                    <LogOut className="h-3 w-3 mr-1" /> Déconnexion
                                                                </Badge>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2 text-sm text-slate-600">
                                                                {log.deviceType === 'mobile' ? <Smartphone className="h-4 w-4" /> :
                                                                    log.deviceType === 'tablet' ? <Tablet className="h-4 w-4" /> : <Monitor className="h-4 w-4" />}
                                                                <span className="capitalize">{log.deviceType || 'Desktop'}</span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <div className="flex items-center justify-end gap-2 text-sm text-slate-600">
                                                                <Globe className="h-4 w-4" />
                                                                <span>{log.browser}</span>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {connectionStats.recentLogs.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                                                            Aucun historique de connexion disponible
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </CardContent>
                            </Card>
                        </>
                    ) : null}
                </TabsContent>
            </Tabs>
        </div>
    );
}
